// WordPress API Service for Headless CMS

const WP_API_URL = import.meta.env.VITE_WP_API_URL || 'http://localhost:8080/wp-json/wp/v2';

export interface WPPost {
  id: number;
  date: string;
  date_gmt: string;
  guid: { rendered: string };
  modified: string;
  modified_gmt: string;
  slug: string;
  status: string;
  type: string;
  link: string;
  title: { rendered: string };
  content: { rendered: string; protected: boolean };
  excerpt: { rendered: string; protected: boolean };
  author: number;
  featured_media: number;
  comment_status: string;
  ping_status: string;
  sticky: boolean;
  template: string;
  format: string;
  meta: any[];
  categories: number[];
  tags: number[];
  _embedded?: {
    author?: any[];
    'wp:featuredmedia'?: any[];
    'wp:term'?: any[][];
  };
}

export interface WPMedia {
  id: number;
  date: string;
  slug: string;
  type: string;
  link: string;
  title: { rendered: string };
  author: number;
  caption: { rendered: string };
  alt_text: string;
  media_type: string;
  mime_type: string;
  media_details: {
    width: number;
    height: number;
    sizes: {
      thumbnail?: { source_url: string };
      medium?: { source_url: string };
      large?: { source_url: string };
      full?: { source_url: string };
    };
  };
  source_url: string;
}

export interface WPCategory {
  id: number;
  count: number;
  description: string;
  link: string;
  name: string;
  slug: string;
  taxonomy: string;
  parent: number;
}

class WordPressAPI {
  private apiUrl: string;
  private headers: HeadersInit;

  constructor() {
    this.apiUrl = WP_API_URL;
    this.headers = {
      'Content-Type': 'application/json',
    };
  }

  // Get all posts with optional parameters
  async getPosts(params?: {
    per_page?: number;
    page?: number;
    categories?: number[];
    tags?: number[];
    search?: string;
    orderby?: 'date' | 'id' | 'include' | 'title' | 'slug';
    order?: 'asc' | 'desc';
    _embed?: boolean;
  }): Promise<WPPost[]> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          if (Array.isArray(value)) {
            queryParams.append(key, value.join(','));
          } else {
            queryParams.append(key, String(value));
          }
        }
      });
    }

    // Only add _embed if explicitly requested (for detail views)
    if (params?._embed) {
      queryParams.append('_embed', 'true');
    }

    // Handle both pretty permalinks and index.php?rest_route= formats
    let url: string;
    if (this.apiUrl.includes('index.php?rest_route=')) {
      url = `${this.apiUrl}/posts&${queryParams.toString()}`;
    } else {
      url = `${this.apiUrl}/posts?${queryParams.toString()}`;
    }
    console.log('Fetching WordPress posts from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      mode: 'cors',
    });

    console.log('WordPress API response:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('WordPress API error:', errorText);
      throw new Error(`Failed to fetch posts: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('WordPress posts received:', data.length, 'posts');
    return data;
  }

  // Get posts with pagination info
  async getPostsPaginated(params?: {
    per_page?: number;
    page?: number;
    categories?: number[];
    tags?: number[];
    search?: string;
    orderby?: 'date' | 'id' | 'include' | 'title' | 'slug';
    order?: 'asc' | 'desc';
    _embed?: boolean;
  }): Promise<{ posts: WPPost[]; totalCount: number }> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          if (Array.isArray(value)) {
            queryParams.append(key, value.join(','));
          } else {
            queryParams.append(key, String(value));
          }
        }
      });
    }

    // Don't add _embed by default for list views (performance)
    if (params?._embed) {
      queryParams.append('_embed', 'true');
    }

    // Handle both pretty permalinks and index.php?rest_route= formats
    let url: string;
    if (this.apiUrl.includes('index.php?rest_route=')) {
      url = `${this.apiUrl}/posts&${queryParams.toString()}`;
    } else {
      url = `${this.apiUrl}/posts?${queryParams.toString()}`;
    }
    console.log('Fetching WordPress posts from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      mode: 'cors',
    });

    console.log('WordPress API response:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('WordPress API error:', errorText);
      throw new Error(`Failed to fetch posts: ${response.status} ${response.statusText}`);
    }

    // Get total count from headers
    const totalCount = parseInt(response.headers.get('X-WP-Total') || '0', 10);
    const totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '0', 10);
    
    const posts = await response.json();
    console.log('WordPress posts received:', posts.length, 'posts');
    console.log('Total posts available:', totalCount);
    console.log('Total pages:', totalPages);
    
    return { posts, totalCount };
  }

  // Get a single post by ID
  async getPost(id: number, embed = true): Promise<WPPost> {
    let url: string;
    if (this.apiUrl.includes('index.php?rest_route=')) {
      const embedParam = embed ? '&_embed=true' : '';
      url = `${this.apiUrl}/posts/${id}${embedParam}`;
    } else {
      const embedParam = embed ? '?_embed=true' : '';
      url = `${this.apiUrl}/posts/${id}${embedParam}`;
    }
    const response = await fetch(url, {
      headers: this.headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch post: ${response.statusText}`);
    }

    return response.json();
  }

  // Get post by slug
  async getPostBySlug(slug: string): Promise<WPPost | null> {
    console.log('Fetching post by slug:', slug);
    
    // Use slug parameter for exact match
    let url: string;
    if (this.apiUrl.includes('index.php?rest_route=')) {
      url = `${this.apiUrl}/posts&slug=${slug}&_embed=true`;
    } else {
      url = `${this.apiUrl}/posts?slug=${slug}&_embed=true`;
    }
    console.log('Fetching from URL:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      mode: 'cors',
    });

    if (!response.ok) {
      console.error('Failed to fetch post by slug:', response.status, response.statusText);
      throw new Error(`Failed to fetch post: ${response.statusText}`);
    }

    const posts = await response.json();
    console.log('Posts found for slug query:', posts.length);
    
    // WordPress API might not filter by slug properly, so we need to filter manually
    if (posts.length > 0) {
      // Find the post with matching slug
      const matchingPost = posts.find((post: WPPost) => post.slug === slug);
      
      if (matchingPost) {
        console.log('Found matching post:', matchingPost.title.rendered);
        return matchingPost;
      }
      
      // If no exact match, log warning and return first post
      console.warn(`No exact slug match for '${slug}', posts returned:`, posts.map((p: WPPost) => p.slug));
      
      // Try to fetch all posts and find the right one
      console.log('Attempting to fetch more posts to find the right one...');
      const allPostsUrl = `${this.apiUrl}/posts?per_page=100&_embed=true`;
      const allPostsResponse = await fetch(allPostsUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
      });
      
      if (allPostsResponse.ok) {
        const allPosts = await allPostsResponse.json();
        const foundPost = allPosts.find((post: WPPost) => post.slug === slug);
        if (foundPost) {
          console.log('Found post in full list:', foundPost.title.rendered);
          return foundPost;
        }
      }
    }
    
    console.error('Post not found for slug:', slug);
    return null;
  }

  // Get all categories
  async getCategories(): Promise<WPCategory[]> {
    const response = await fetch(`${this.apiUrl}/categories?per_page=100`, {
      headers: this.headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch categories: ${response.statusText}`);
    }

    return response.json();
  }

  // Get all tags
  async getTags(): Promise<any[]> {
    const response = await fetch(`${this.apiUrl}/tags?per_page=100`, {
      headers: this.headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch tags: ${response.statusText}`);
    }

    return response.json();
  }

  // Get media by ID
  async getMedia(id: number): Promise<WPMedia> {
    const response = await fetch(`${this.apiUrl}/media/${id}`, {
      headers: this.headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch media: ${response.statusText}`);
    }

    return response.json();
  }

  // Search posts
  async searchPosts(query: string): Promise<WPPost[]> {
    return this.getPosts({ 
      search: query, 
      per_page: 20,
      _embed: true 
    });
  }

  // Get recent posts
  async getRecentPosts(limit = 5): Promise<WPPost[]> {
    return this.getPosts({ 
      per_page: limit, 
      orderby: 'date', 
      order: 'desc',
      _embed: true
    });
  }

  // Get posts by category
  async getPostsByCategory(categoryId: number, limit = 10): Promise<WPPost[]> {
    return this.getPosts({ 
      categories: [categoryId], 
      per_page: limit,
      _embed: true
    });
  }

  // Helper function to extract featured image URL
  getFeaturedImageUrl(post: WPPost, size: 'thumbnail' | 'medium' | 'large' | 'full' = 'full'): string | null {
    if (!post._embedded?.['wp:featuredmedia']?.[0]) {
      return null;
    }

    const media = post._embedded['wp:featuredmedia'][0];
    
    if (size === 'full') {
      return media.source_url;
    }

    return media.media_details?.sizes?.[size]?.source_url || media.source_url;
  }

  // Helper function to get author name
  getAuthorName(post: WPPost): string {
    return post._embedded?.author?.[0]?.name || 'Unknown Author';
  }

  // Helper function to strip HTML tags from content
  stripHtml(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  // Helper function to create excerpt
  createExcerpt(content: string, length = 150): string {
    const stripped = this.stripHtml(content);
    if (stripped.length <= length) return stripped;
    return stripped.substring(0, length).trim() + '...';
  }
}

// Export singleton instance
export const wpApi = new WordPressAPI();

// Export class for testing or multiple instances
export { WordPressAPI };